/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import {
	DndProvider,
	Tree,
	MultiBackend,
	getBackendOptions,
} from '@minoru/react-dnd-treeview'
import { DndTreeNode } from './DndTreeNode'
import { NodeDragPreview } from './NodeDragPreview'
import { Placeholder } from './Placeholder'
import { mono } from '@components/systems/backoffice/backoffice.tokens'

import { MenuTreeNodeType } from '@redux/types'

interface PropsType {
	data: MenuTreeNodeType[]
	rootId: string | number
	onRadioChange?: (value: string | number) => void
	showCheckBox?: boolean
	showRadiobutton?: boolean
	hideObject?: boolean
	disableDragging?: boolean
	showDragHandle?: boolean
	onNodeClick?: (node: MenuTreeNodeType) => void
	selectedRadio?: string
	onDrop?: (newTree: MenuTreeNodeType[]) => void
	selectedMenuId?: string
	nodeRightAccessory?: (node: MenuTreeNodeType) => JSX.Element
	style?: React.CSSProperties
	nodeStyle?: React.CSSProperties
	nodeClassName?: string
}

/**
 * 드래그 트리. 안쪽에서 deepPurple 테마를 따로 만들던 것을 걷어냈다 — 바깥 BackofficeTheme 을 그대로 쓴다.
 * 드롭 대상은 옅은 잉크 면, 자리표시는 1px ink 선.
 */
export const DndTree = (props: PropsType) => {
	// state
	const [treeData, setTreeData] = React.useState<MenuTreeNodeType[]>(props.data)
	const [selectedRadio, setSelectedRadio] = React.useState<string | number>('')

	React.useEffect(() => {
		setTreeData(props.data)
	}, [props.data])

	// variables
	const showCheckBox: boolean = props.showCheckBox ?? false
	const showRadiobutton: boolean = props.showRadiobutton ?? false
	const hideObject: boolean = props.hideObject ?? false
	const disableDragging: boolean =
		(props.disableDragging ?? false) && (props.showDragHandle ?? false)
	const showRootChevronButton: boolean =
		treeData.findIndex(aTree => aTree.parent === 'root' && aTree.droppable) > -1

	const onDropEnd = React.useCallback(
		(newTree: MenuTreeNodeType[]) => {
			if (!disableDragging && props.onDrop !== undefined) {
				props.onDrop(newTree)
				setTreeData(newTree)
			}
		},
		[disableDragging, props.onDrop],
	)

	const onRadioChange = React.useCallback(
		(value: string | number) => {
			setSelectedRadio(value)
			if (props.onRadioChange) {
				props.onRadioChange(value)
			}
		},
		[props.onRadioChange],
	)

	return (
		<DndProvider backend={MultiBackend} options={getBackendOptions()}>
			<View css={style}>
				<Tree
					tree={treeData}
					rootId={props.rootId}
					sort={false}
					insertDroppableFirst={false}
					dropTargetOffset={5}
					canDrag={() => !disableDragging}
					canDrop={(tree, { dragSource, dropTargetId }) => {
						if (!disableDragging) {
							if (dragSource?.parent === dropTargetId) {
								return true
							}
						} else {
							return false
						}
					}}
					render={(node: MenuTreeNodeType, options) => {
						const childrenNode: MenuTreeNodeType[] = treeData.filter(aNode =>
							hideObject
								? aNode.parent === node.id && aNode.droppable
								: aNode.parent === node.id,
						)
						return (
							<DndTreeNode
								node={node}
								hasChildren={childrenNode.length > 0}
								childrenNodeList={childrenNode}
								{...options}
								showCheckBox={showCheckBox}
								showRadiobutton={showRadiobutton}
								hideObject={hideObject}
								showDragHandle={props.showDragHandle ?? false}
								selectedRadio={props.selectedRadio ?? selectedRadio}
								onRadioChange={onRadioChange}
								onNodeClick={props.onNodeClick}
								disableDragging={disableDragging}
								showRootChevronButton={showRootChevronButton}
								selectedMenuId={props.selectedMenuId}
								nodeRightAccessory={props.nodeRightAccessory}
								style={props.nodeStyle}
								className={props.nodeClassName}
							/>
						)
					}}
					dragPreviewRender={monitorProps => (
						<NodeDragPreview monitorProps={monitorProps} />
					)}
					placeholderRender={(node, { depth }) => (
						<Placeholder node={node} depth={depth} />
					)}
					onDrop={onDropEnd}
					classes={{
						root: 'treeRoot',
						draggingSource: 'draggingSource',
						dropTarget: 'dropTarget',
						placeholder: 'placeholderContainer',
					}}
				/>
			</View>
		</DndProvider>
	)
}

const style = css({
	width: '100%',
	height: '100%',
	padding: 0,

	'.treeRoot': {
		height: '100%',
		margin: 0,
		padding: 0,
		boxSizing: 'border-box',

		ul: {
			padding: 0,
		},

		li: {
			listStyle: 'none',
		},
	},

	'.draggingSource': {
		opacity: 0.3,
	},

	'.dropTarget': {
		backgroundColor: 'rgba(22, 24, 27, 0.04)',
	},

	'.placeholderContainer': {
		position: 'relative',
	},

	'& .text': { fontFamily: 'inherit' },
	'& .MuiTouchRipple-root': { display: 'none' },
	'& .MuiRadio-root': { color: mono.lineInput },
})
